import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';
import { WeightDiagram } from '../../components/Diagrams';

export default function TrailerTotalvaegt() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Kørekort og vægt", path: "/trailer-regler" },
    { name: "Totalvægt og egenvægt", path: "/trailer-totalvaegt" }
  ];
  const tocItems = [
    { id: "hvad-betyder-tallene", title: "Hvad betyder totalvægt, egenvægt og lasteevne?" },
    { id: "vaegtskiltet", title: "Sådan aflæser du trailerens typeskilt" },
    { id: "750-kg-graensen", title: "Hvorfor er 750 kg-grænsen så vigtig?" },
    { id: "vejekontrol-overvaegt", title: "Vejekontrol og bøder for overlæs" },
    { id: "nedvejning", title: "Nedvejning af trailer: Fordele og fremgangsmåde" }
  ];

  const faq = [
    {
      q: "Hvad er forskellen på egenvægt og køreens vægt?",
      a: "Egenvægt er trailerens egen vægt helt tom uden noget gods på ladet. Køreens vægt eller faktisk totalvægt er trailerens egenvægt plus vægten af det gods, du har læsset på traileren."
    },
    {
      q: "Hvor meget overlæs må man køre med uden bøde?",
      a: "Juridisk er der en nultolerance. Politiet tolererer ingen procentvis overskridelse, og bøden beregnes ud fra, hvor mange procent trailerens tilladte totalvægt eller tilladte akseltryk er overskredet."
    },
    {
      q: "Kan man få nedvejet en 750 kg eller 1.500 kg trailer?",
      a: "Ja. En trailer kan nedvejes hos en synsvirksomhed og registreres med en lavere tilladt totalvægt i Motorregistret. Det kan give fordele i forhold til B-kørekortets 3.500 kg-grænse eller spare vægtafgift."
    },
    {
      q: "Hvad sker der, hvis vægtskiltet mangler på traileren?",
      a: "Hvis trailerens fabriksskilt eller typegodkendelsesskilt mangler, kan traileren kasseres ved syn, og politiet kan inddrage nummerpladen ved kontrol, da vægtene ikke kan verificeres entydigt."
    }
  ];

  const sources = [
    { title: "Dimensionsbekendtgørelsen (BEK nr. 577 af 30/03/2021)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2021/577" },
    { title: "Motorstyrelsen: Regler for trailer og påhængsvogn", source: "motorst.dk", url: "https://motorst.dk/borger/nummerplader/trailer" }
  ];

  return (
    <>
      <SEO 
        title="Trailer totalvægt, egenvægt og lasteevne forklaret" 
        description="Lær hvad totalvægt, egenvægt og lasteevne betyder på trailerens typeskilt. Forstå 750 kg-grænsen, overlæs og regler for nedvejning." 
        pathname="/trailer-totalvaegt"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Totalvægt, egenvægt og lasteevne på trailer
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          Trailerens værdier er opdelt i tre faste tal: <em>Tilladt totalvægt</em> (det maksimale traileren må veje med gods), <em>egenvægt</em> (trailerens vægt i tom tilstand) og <em>lasteevne</em> (forskellen mellem totalvægt og egenvægt).
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          750 kg er den mest centrale vægtgrænse i dansk færdselslov: Op til 750 kg må du altid køre på almindeligt B-kørekort, uanset trækbilens tilladte totalvægt, og traileren er fritaget for periodisk syn og vægtafgift (så længe den er 500 kg eller under).
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="Simpel beregning af lasteevne">
          <p className="m-0 font-mono text-[16px]">
            <strong>Tilladt totalvægt – Egenvægt = Maksimal lasteevne (nyttelast)</strong>
          </p>
          <p className="text-[14px] text-[#5C636B] m-0 mt-1">
            Eksempel: En 750 kg trailer med en egenvægt på 180 kg må højst læsses med 570 kg gods.
          </p>
        </Callout>

        <h2 id="hvad-betyder-tallene" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvad betyder totalvægt, egenvægt og lasteevne?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          For at undgå farlige situationer og store bøder ved vejkontroller er det afgørende at kende forskel på de tre tekniske vægtbegreber:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Begreb</th>
                <th className="py-2.5 pr-4 font-semibold">Beskrivelse</th>
                <th className="py-2.5 font-semibold">Hvor findes det?</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Tilladt totalvægt</td>
                <td className="py-3 pr-4">Maksimal lovlig vægt for trailer plus alt gods</td>
                <td className="py-3 text-[#5C636B]">Registreringsattest &amp; typeskilt</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Egenvægt</td>
                <td className="py-3 pr-4">Trailerens egen vægt uden last og udstyr</td>
                <td className="py-3 text-[#5C636B]">Registreringsattest &amp; typeskilt</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Lasteevne (nyttelast)</td>
                <td className="py-3 pr-4">Hvor mange kilo gods du lovligt må læsse</td>
                <td className="py-3 text-[#5C636B]">Totalvægt minus egenvægt</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Faktisk totalvægt</td>
                <td className="py-3 pr-4">Hvad traileren vejer lige nu på en vægt</td>
                <td className="py-3 text-[#5C636B]">Kontrolvejning hos politiet</td>
              </tr>
            </tbody>
          </table>
        </div>

        <WeightDiagram caption="Figur 1: Sammenhængen mellem egenvægt, nyttelast og tilladt totalvægt på traileren." />

        <h2 id="vaegtskiltet" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Sådan aflæser du trailerens typeskilt
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Alle godkendte trailere har et metal- eller mærkattypeskilt nittet eller klæbet fast på trækstangen eller chassisrammen. Skiltet indeholder fabrikantens navn, stelnummer (VIN) samt de godkendte aksel- og totalvægte.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          På skiltet angives typisk:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Tilladt totalvægt (Total weight / Zul. Gesamtgewicht):</strong> F.eks. 750 kg eller 1.500 kg.</li>
          <li><strong>Akseltryk (Axle load / Achslast):</strong> Den maksimale belastning hjulakslen må bære.</li>
          <li><strong>Maksimalt kugletryk (S-værdi / Stützlast):</strong> Det maksimale lodrette tryk på koblingshovedet, typisk 75 eller 100 kg.</li>
        </ul>

        <h2 id="750-kg-graensen" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvorfor er 750 kg-grænsen så vigtig?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I færdselslovgivningen defineres påhængskøretøjer med en tilladt totalvægt på højst 750 kg som "lette påhængskøretøjer". Denne grænse udløser en række afgørende fordele:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Kørekort B:</strong> Du må altid trække en 750 kg trailer med almindeligt B-kørekort, uanset om bilen vejer 3.500 kg (samlet vogntogsvægt 4.250 kg). Se detaljerne i <Link to="/koerekort-og-trailer" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">kørekort og trailer</Link>.</li>
          <li><strong>Intet periodisk syn:</strong> Let påhængskøretøj under 750 kg skal ikke til periodisk syn (medmindre det er godkendt til Tempo 100). Se <Link to="/trailer-syn" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">synsregler for trailer</Link>.</li>
          <li><strong>Bremsekrav:</strong> Trailere op til 750 kg behøver ikke være forsynet med driftsbremser, såfremt bilens registreringsattest godkender ubremset påhængsvogn af den pågældende vægt.</li>
        </ul>

        <h2 id="vejekontrol-overvaegt" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Vejekontrol og bøder for overlæs
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Overlæs på en trailer straffes med faste bødetakster pr. påbegyndt procent overskridelse. Hvis traileren vejer mere end den tilladte totalvægt, koster det en fast bøde til både føreren og eventuelt ejeren af traileren.
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Procent overlæs</th>
                <th className="py-2.5 pr-4 font-semibold">Konsekvens</th>
                <th className="py-2.5 font-semibold">Frakendelse / klip</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4">Under 7%</td>
                <td className="py-3 pr-4">Bøde pr. procent overskridelse</td>
                <td className="py-3 text-[#5C636B]">Ingen klip</td>
              </tr>
              <tr>
                <td className="py-3 pr-4">Over 7%</td>
                <td className="py-3 pr-4">Forhøjet bøde, køreforbud indtil omlæsning</td>
                <td className="py-3 text-[#5C636B]">Ingen klip</td>
              </tr>
              <tr>
                <td className="py-3 pr-4">Over 30%</td>
                <td className="py-3 pr-4">Betydelig bøde, absolut kørselsforbud</td>
                <td className="py-3 font-semibold text-[#B58200]">Betinget frakendelse af førerretten</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Husk også, at forkert placering af lasten kan overskride det tilladte <Link to="/kugletryk" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">kugletryk</Link> eller akseltrykket, selvom totalvægten ikke er overskredet.
        </p>

        <h2 id="nedvejning" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Nedvejning af trailer: Fordele og fremgangsmåde
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Hvis du har en trailer med en tilladt totalvægt på f.eks. 1.000 kg, men din bils tilladte totalvægt er 2.600 kg, overstiger det samlede vogntog 3.500 kg (2.600 + 1.000 = 3.600 kg). Det kræver B+ eller BE-kørekort.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          I denne situation kan du få traileren <em>nedvejet</em> hos en godkendt synshal til f.eks. 900 kg. Derved bliver den samlede tilladte totalvægt 3.500 kg, og du må køre lovligt med dit almindelige B-kørekort. Nedvejning koster et registreringssyn i synshallen samt ændring af registreringsattesten hos Motorstyrelsen.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs mere i vores artikler om <Link to="/koerekort-og-trailer" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">kørekortreglerne</Link> og <Link to="/trailer-syn" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">syn af trailer</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
